import { zodResolver } from '@hookform/resolvers/zod';
import { LoginRequestSchema, type LoginRequest } from '@utazzo/shared';
import { UzAlert, UzButton, UzForm, UzFormField, UzInput, useBranding } from '@utazzo/ui';
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { IDLE_LOGGED_OUT_KEY } from '../idle/useIdleLogout';
import { useLogin } from '../use-auth';

import { AuthLayout } from './AuthLayout';

export const LoginPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const branding = useBranding();
  const login = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  // Shown when the user landed here via an inactivity auto-logout, so they know
  // why. Read once on mount; survives a reload until dismissed or signed in.
  const [loggedOutInactive, setLoggedOutInactive] = useState(
    () => sessionStorage.getItem(IDLE_LOGGED_OUT_KEY) === '1',
  );
  const dismissInactivityNotice = (): void => {
    sessionStorage.removeItem(IDLE_LOGGED_OUT_KEY);
    setLoggedOutInactive(false);
  };

  const form = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = form.handleSubmit((values) => {
    login.mutate(values);
  });

  return (
    <AuthLayout
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle', { appName: branding.appName })}
      footer={t('auth.login.noAccountHint')}
    >
      {loggedOutInactive && (
        <UzAlert
          tone="warning"
          closable
          onClose={dismissInactivityNotice}
          title={t('auth.login.inactivityTitle')}
          description={t('auth.login.inactivityBody')}
          className="mb-4"
        />
      )}

      <UzForm form={form} onSubmit={onSubmit}>
        <UzFormField name="email">
          {({ field, error }) => (
            <UzInput
              {...field}
              type="text"
              autoComplete="username"
              label={t('auth.login.emailLabel')}
              placeholder={t('auth.login.emailPlaceholder')}
              leftAdornment={<Mail className="h-4 w-4" aria-hidden />}
              error={error}
              required
              disabled={login.isPending}
              autoFocus
            />
          )}
        </UzFormField>

        <UzFormField name="password">
          {({ field, error }) => (
            <UzInput
              {...field}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              label={t('auth.login.passwordLabel')}
              placeholder={t('auth.login.passwordPlaceholder')}
              leftAdornment={<Lock className="h-4 w-4" aria-hidden />}
              rightAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-muted-foreground hover:text-foreground -m-1 p-1 transition-colors"
                  aria-label={
                    showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')
                  }
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                </button>
              }
              error={error}
              required
              disabled={login.isPending}
            />
          )}
        </UzFormField>

        <div className="flex items-center justify-end pt-1">
          <Link to="/forgot-password" className="text-primary text-sm font-medium hover:underline">
            {t('auth.login.forgotPassword')}
          </Link>
        </div>

        <UzButton
          type="submit"
          fullWidth
          loading={login.isPending}
          rightIcon={<ArrowRight className="h-4 w-4" aria-hidden />}
          className="mt-2"
        >
          {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
        </UzButton>
      </UzForm>
    </AuthLayout>
  );
};
